'use client';

import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Badge } from 'antd';
import type { MenuProps } from 'antd';
import {
  ShoppingOutlined,
  ScheduleOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  CalendarOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useRouter, usePathname } from 'next/navigation';

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // 側邊欄選單項目 - 核心功能
  const menuItems: MenuProps['items'] = [
    {
      key: '/bookings',
      icon: <ShoppingOutlined />,
      label: '訂單管理',
    },
    {
      key: '/properties',
      icon: <HomeOutlined />,
      label: '房源管理',
    },
    {
      key: '/availability',
      icon: <CalendarOutlined />,
      label: '空房與房價設定',
    },
    {
      key: '/housekeeping',
      icon: <ScheduleOutlined />,
      label: '清掃排程',
    },
    {
      key: '/webhook-logs',
      icon: <FileTextOutlined />,
      label: 'OTA 紀錄檢查',
    },
  ];

  // 用戶下拉選單
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '個人資料',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '登出',
      onClick: () => {
        // 實際應用中應該清除登入狀態
        router.push('/login');
      },
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    router.push(e.key);
  };

  return (
    <Layout className="min-h-screen bg-gray-50">
      {/* 側邊欄 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="light"
        className="shadow-md"
      >
        <div className="h-16 flex items-center justify-center border-b border-gray-200 bg-white">
          <h1
            className={`font-bold text-gray-800 transition-all duration-300 ${
              collapsed ? 'text-xs' : 'text-lg'
            }`}
          >
            {collapsed ? 'PMS' : 'Sway PMS'}
          </h1>
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          className="border-r-0 bg-white"
        />
      </Sider>

      {/* 主要內容區域 */}
      <Layout className="bg-gray-50">
        {/* 頂部導航 */}
        <Header className="bg-white px-4 flex items-center justify-between border-b border-gray-200 shadow-sm">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-base w-16 h-16 hover:bg-gray-100 transition-colors duration-200"
          />

          <div className="flex items-center space-x-4">
            {/* 通知鈴鐺 */}
            <Badge
              count={5}
              className="hover:scale-105 transition-transform duration-200"
            >
              <Button
                type="text"
                icon={<BellOutlined />}
                size="large"
                className="hover:bg-gray-100"
              />
            </Badge>

            {/* 用戶下拉選單 */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
            >
              <div className="flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                <Avatar
                  icon={<UserOutlined />}
                  className="bg-blue-500"
                />
                <span className="text-gray-700 font-medium">管理員</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* 內容區域 */}
        <Content className="m-6 p-6 min-h-72 bg-white rounded-lg shadow-sm border border-gray-200">
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
